import {
  Box,
  Button,
  FileButton,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';

type Note = {
  id: string;
  author: string;
  message: string;
  timestamp: string;
  actions: string[];
  attachments: {
    id: string;
    fileName: string;
    mimeType: string;
    size: number;
    createdAt: string;
  }[];
};

function getResolvedTimeZone(timeZone: string | null) {
  if (!timeZone) {
    return null;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return timeZone;
  } catch {
    return null;
  }
}

function CommentBox({
  author,
  message,
  timestamp,
  actions,
  attachments,
  onEdit,
  onOpenAttachment,
}: Note & {
  onEdit: (message: string) => void;
  onOpenAttachment: (attachment: Note['attachments'][number]) => void;
}) {
  const [editMode, editModeFunc] = useDisclosure(false);
  const [editedMessage, setEditedMessage] = useState(message);

  const viewerTimeZone = useMemo(
    () => getResolvedTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone),
    [],
  );

  const luxonTimestamp = DateTime.fromISO(timestamp, {
    zone: viewerTimeZone ?? 'UTC',
  });

  return (
    <Paper p="md" withBorder>
      {!editMode ? (
        <Group align="flex-start" wrap="nowrap">
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group justify="space-between">
              <Text fw={600} size="sm">
                {author}
              </Text>
              {actions.includes('edit') ? (
                <Button
                  variant="transparent"
                  size="compact-xs"
                  onClick={() => editModeFunc.open()}
                >
                  Edit
                </Button>
              ) : null}
            </Group>

            <Text
              size="sm"
              mt={4}
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {message}
            </Text>

            {attachments.length ? (
              <Stack gap={4} mt="sm">
                {attachments.map((attachment) => (
                  <Button
                    key={attachment.id}
                    variant="light"
                    size="compact-sm"
                    justify="space-between"
                    onClick={() => onOpenAttachment(attachment)}
                  >
                    <Text size="xs" truncate="end" maw={220}>
                      {attachment.fileName}
                    </Text>
                    <Text size="xs" c="dimmed" ml="sm">
                      {formatFileSize(attachment.size)}
                    </Text>
                  </Button>
                ))}
              </Stack>
            ) : null}

            <Text size="xs" c="dimmed" mt={8}>
              {luxonTimestamp.toLocaleString(DateTime.DATETIME_FULL)}
            </Text>
          </Box>
        </Group>
      ) : (
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Stack gap="sm">
            <Textarea
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
            />
            <Group wrap="nowrap">
              <Button
                size="compact-sm"
                fullWidth
                color="gray"
                variant="outline"
                onClick={() => editModeFunc.close()}
              >
                Cancel
              </Button>
              <Button
                size="compact-sm"
                fullWidth
                onClick={() => {
                  onEdit(editedMessage);
                  editModeFunc.close();
                }}
              >
                Save
              </Button>
            </Group>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}

type NotesPanelProps = {
  notes: Note[];
  loading: boolean;
  canUpload: boolean;
  createNote: (message: string, files: File[]) => Promise<void>;
  updateNote: (noteId: string, message: string) => void;
  openAttachment: (
    noteId: string,
    attachment: Note['attachments'][number],
  ) => void;
  onClose: () => void;
  showHeader?: boolean;
};

export function NotesPanel({
  notes,
  loading,
  canUpload,
  createNote,
  updateNote,
  openAttachment,
  onClose,
  showHeader,
}: NotesPanelProps) {
  const [value, setValue] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitNote = async () => {
    const message = value.trim();
    if (!message) return;

    setSubmitting(true);
    setFileError(null);
    try {
      await createNote(message, files);
      setValue('');
      setFiles([]);
    } catch {
      setFileError('The note could not be created. Your draft was preserved.');
    } finally {
      setSubmitting(false);
    }
  };

  const addFiles = (selectedFiles: File[]) => {
    const nextFiles = [...files, ...selectedFiles];
    if (nextFiles.length > MAX_ATTACHMENTS_PER_NOTE) {
      setFileError(`You can attach up to ${MAX_ATTACHMENTS_PER_NOTE} files.`);
      return;
    }
    const invalid = selectedFiles.find(
      (file) =>
        file.size > MAX_ATTACHMENT_SIZE_BYTES ||
        !isAllowedAttachment(file.name),
    );
    if (invalid) {
      setFileError(
        invalid.size > MAX_ATTACHMENT_SIZE_BYTES
          ? `${invalid.name} exceeds the 10 MB limit.`
          : `${invalid.name} is not a supported file type.`,
      );
      return;
    }
    setFiles(nextFiles);
    setFileError(null);
  };

  const busy = loading || submitting;

  return (
    <Stack
      gap="md"
      h="100%"
      style={{
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      {(showHeader === undefined || showHeader) && (
        <Group justify="space-between">
          <Title order={4}>Notes</Title>

          <Button size="xs" variant="transparent" onClick={onClose}>
            Close
          </Button>
        </Group>
      )}

      <Stack gap="xs">
        <Group align="flex-end" wrap="nowrap">
          <Textarea
            placeholder="Write a note..."
            disabled={busy}
            value={value}
            onChange={(event) => setValue(event.currentTarget.value)}
            autosize
            minRows={1}
            maxRows={4}
            style={{ flex: 1 }}
          />

          <Button onClick={() => void submitNote()} disabled={busy}>
            Submit
          </Button>
        </Group>

        {canUpload ? (
          <Group gap="xs">
            <FileButton
              onChange={(selected) => addFiles(selected ?? [])}
              accept={ATTACHMENT_ACCEPT}
              multiple
            >
              {(props) => (
                <Button
                  {...props}
                  variant="outline"
                  size="compact-sm"
                  disabled={busy}
                >
                  Attach files
                </Button>
              )}
            </FileButton>
            <Text size="xs" c="dimmed">
              Up to 5 files, 10 MB each
            </Text>
          </Group>
        ) : null}

        {files.length ? (
          <Stack gap={4}>
            {files.map((file, index) => (
              <Group
                key={`${file.name}-${file.size}-${index}`}
                justify="space-between"
                wrap="nowrap"
              >
                <Text size="xs">
                  {file.name} ({formatFileSize(file.size)})
                </Text>
                <Button
                  variant="transparent"
                  size="compact-xs"
                  disabled={busy}
                  onClick={() =>
                    setFiles((current) => current.filter((_, i) => i !== index))
                  }
                >
                  Remove
                </Button>
              </Group>
            ))}
          </Stack>
        ) : null}

        {fileError ? (
          <Text size="xs" c="red">
            {fileError}
          </Text>
        ) : null}
      </Stack>

      {/* Scrollable middle */}
      <ScrollArea
        style={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack gap="sm">
          {loading ? (
            <Loader />
          ) : (
            notes.map((note) => (
              <CommentBox
                key={note.id}
                {...note}
                onEdit={(message) => updateNote(note.id, message)}
                onOpenAttachment={(attachment) =>
                  openAttachment(note.id, attachment)
                }
              />
            ))
          )}
        </Stack>
      </ScrollArea>

      {/* Footer input */}
    </Stack>
  );
}

const MAX_ATTACHMENTS_PER_NOTE = 5;
const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
]);
const ATTACHMENT_ACCEPT = Array.from(ALLOWED_ATTACHMENT_EXTENSIONS)
  .map((extension) => `.${extension}`)
  .join(',');

function isAllowedAttachment(fileName: string): boolean {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return Boolean(extension && ALLOWED_ATTACHMENT_EXTENSIONS.has(extension));
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
